import React, { useContext, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { WebSocketContext } from "./WebSocketContext";
import { useTheme } from "./contexts/ThemeContext";
import StockCard from "./components/StockCard";
import "./StockList.css"; // Add CSS for the stock list
import BottomSheet from './components/BottomSheet';
import BottomSheetMini from './components/BottomSheetMini';
import searchIcon from './assets/search.svg';
import {Search, LiveTv, CurrencyRupee, Add, Edit, Save, Cancel, Delete, ViewList, TrendingUp} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Refresh } from '@mui/icons-material';
import config from './config';

const StockList = () => {
  const { stocks, userScripts, userDetails, positions, netPositions, lastUpdateTime, fetchUserScriptsExternal } = useContext(WebSocketContext);
  const { isDarkMode, toggleTheme } = useTheme();
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [filter, setFilter] = useState("MCX FUT"); // Added filter state
  const [showMoneyOptions, setShowMoneyOptions] = useState(false);
  const [proView, setProView] = useState(() => {
    // Load view preference from localStorage
    const savedView = localStorage.getItem('proViewPreference');
    return savedView ? JSON.parse(savedView) : true; // Default to pro view
  });
  const [oneClickTrade, setOneClickTrade] = useState(() => {
    // Load one click trade preference from localStorage
    const savedOneClick = localStorage.getItem('oneClickTradePreference');
    return savedOneClick ? JSON.parse(savedOneClick) : false;
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const [showTv, setShowTv] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [reorderedScripts, setReorderedScripts] = useState([]);
  const [savedOrder, setSavedOrder] = useState({});
  const [expandedCard, setExpandedCard] = useState(null);
  const [orderType, setOrderType] = useState('Limit');
  const [lotSize, setLotSize] = useState(1);
  const [quantity, setQuantity] = useState(100);
  const [rate, setRate] = useState('');
  const [disableBtn, setDisable] = useState(false);
  const prevLotsRef = useRef(lotSize);
  const prevQuantityRef = useRef(quantity);

  // Lot and Quantity management functions (from BottomSheet)
  const incrementLots = () => {
    setLotSize(lotSize + 1);
  };

  const decrementLots = () => {
    if (lotSize > 1) {
      setLotSize(lotSize - 1);
    }
  };

  const incrementQuantity = () => {
    if (expandedCard) {
      const stock = displayScripts.find(s => s.instrumentIdentifier === expandedCard);
      if (stock && stock.exchange !== "OPTSTK") {
        setQuantity(Number(quantity) + 1);
      }
    }
  };

  const decrementQuantity = () => {
    if (expandedCard) {
      const stock = displayScripts.find(s => s.instrumentIdentifier === expandedCard);
      if (stock && stock.exchange !== "OPTSTK") {
        if (quantity > 1) {
          setQuantity(Number(quantity) - 1);
        }
      }
    }
  };

  const handleQuantityChange = (event) => {
    setQuantity(parseInt(event.target.value) || 0);
  };

  // Update quantity when lot size changes
  useEffect(() => {
    if (expandedCard && prevLotsRef.current !== lotSize) {
      const stock = displayScripts.find(s => s.instrumentIdentifier === expandedCard);
      if (stock) {
        const newQuantity = lotSize * (stock.lotsize || 1);
        setQuantity(newQuantity);
        prevLotsRef.current = lotSize;
        prevQuantityRef.current = newQuantity;
      }
    }
  }, [lotSize, expandedCard]);

  // Update lot size when quantity changes (only for manual quantity input)
  useEffect(() => {
    if (expandedCard && prevQuantityRef.current !== quantity) {
      const stock = displayScripts.find(s => s.instrumentIdentifier === expandedCard);
      if (stock && stock.exchange !== "OPTSTK") {
        const newLots = quantity / (stock.lotsize || 1);
        setLotSize(newLots);
        prevQuantityRef.current = quantity;
        prevLotsRef.current = newLots;
      }
    }
  }, [quantity, expandedCard]);

  // Reset refs when expanded card changes
  useEffect(() => {
    if (expandedCard) {
      prevLotsRef.current = lotSize;
      prevQuantityRef.current = quantity;
    }
  }, [expandedCard]);

  const handleNavigation = (path) => () => {
    navigate(path);
  };

  // Local storage functions for script order
  const saveScriptOrder = (order) => {
    try {
      localStorage.setItem('scriptOrder', JSON.stringify(order));
      console.log('Script order saved to localStorage:', order);
    } catch (error) {
      console.error('Error saving script order:', error);
    }
  };

  const loadScriptOrder = () => {
    try {
      const saved = localStorage.getItem('scriptOrder');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading script order:', error);
      return {};
    }
  };

  const getScriptOrder = (exchange) => {
    return savedOrder[exchange] || [];
  };

  const updateScriptOrder = (exchange, newOrder) => {
    const updatedOrder = {
      ...savedOrder,
      [exchange]: newOrder
    };
    setSavedOrder(updatedOrder);
    saveScriptOrder(updatedOrder);
  };

  // Function to toggle pro view and save preference
  const toggleProView = () => {
    const newProView = !proView;
    setProView(newProView);
    // Save preference to localStorage
    try {
      localStorage.setItem('proViewPreference', JSON.stringify(newProView));
      console.log('View preference saved:', newProView ? 'Pro View' : 'Simple View');
    } catch (error) {
      console.error('Error saving view preference:', error);
    }
  };

  // Function to toggle one click trade and save preference
  const toggleOneClickTrade = () => {
    const newOneClickTrade = !oneClickTrade;
    setOneClickTrade(newOneClickTrade);
    // Save preference to localStorage
    try {
      localStorage.setItem('oneClickTradePreference', JSON.stringify(newOneClickTrade));
      console.log('One Click Trade preference saved:', newOneClickTrade);
    } catch (error) {
      console.error('Error saving one click trade preference:', error);
    }
  };

  // Function to remove script from user's list
  const removeScriptFromUser = async (script) => {
    try {
      const clientId = localStorage.getItem('userId');
      
      const requestBody = {
        clientId: clientId,
        stockId: script.instrumentIdentifier
      };

      const response = await fetch(`${config.API_BASE_URL}removeScriptFromUser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer 7c85cfc0ad2328331d4784ffedb97565154cd2016ed0b2b6843a53e7f2a2343d`
        },
        body: JSON.stringify(requestBody),
      });
      
      const data = await response.json();
      console.log('Remove script response:', data);
      
      if (data.success) {
        // Refresh the user scripts list
        fetchUserScriptsExternal();
        console.log('Script removed successfully:', script.dispname);
      } else {
        console.error('Failed to remove script:', data.message);
        alert('Failed to remove script: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error removing script:', error);
      alert('Error removing script: ' + error.message);
    }
  };

  // Function to handle script deletion
  const handleDeleteScript = (script, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const confirmDelete = window.confirm(
      `Are you sure you want to remove "${script.dispname}" from your watchlist?`
    );
    
    if (confirmDelete) {
      removeScriptFromUser(script);
    }
  };

  const handleToggle = () => {
    setShowTv(prevState => !prevState);
  };

  const handleCardClick = useCallback((stock) => {
    if (isEditMode) return; // Don't expand in edit mode
    
    const _stock = stocks[stock.instrument_token] || {};
    const expandedStock = {
      ...stock,
      live: _stock,
      buyPrice: _stock.Bid || stock.buyPrice || 0,
      sellPrice: _stock.Ask || stock.sellPrice || 0,
      open: _stock.Open || stock.open || 0,
      high: _stock.High || stock.high || 0,
      low: _stock.Low || stock.low || 0,
      close: _stock.Close || stock.close || 0,
      lastTradedPrice: _stock.LTP || stock.lastTradedPrice || 0,
    };
    
    setExpandedCard(expandedCard === stock.instrumentIdentifier ? null : stock.instrumentIdentifier);
    setRate(_stock.LTP ? _stock.LTP.toFixed(2) : '');
  }, [isEditMode, stocks, expandedCard]);

  const handleCloseSheet = () => {
    setShowBottomSheet(false);
    setSelectedStock(null);
    // alert("closed"+showBottomSheet);
  };

  // Function to get the latest stock data for the selected stock
  const getLatestStockData = (stock) => {
    return stocks[stock.instrument_token] || {};
  };

  // Effect to update the selected stock data in the bottom sheet whenever stocks context updates
  useEffect(() => {
    if (selectedStock) {
      const latestStockData = getLatestStockData(selectedStock);
      setSelectedStock((prevStock) => ({
        ...prevStock,
        live: latestStockData,
      }));
    }
  }, [stocks]);

  // Effect to load saved script order on component mount
  useEffect(() => {
    const loadedOrder = loadScriptOrder();
    setSavedOrder(loadedOrder);
  }, []);

  // Effect to listen for edit mode toggle from TitleBar
  useEffect(() => {
    const handleEditModeToggle = () => {
      handleEditMode();
    };

    window.addEventListener('toggleEditMode', handleEditModeToggle);
    return () => {
      window.removeEventListener('toggleEditMode', handleEditModeToggle);
    };
  }, []);

  // Listen for trade bottom sheet events from sticky scripts
  useEffect(() => {
    const handleOpenTradeBottomSheet = (event) => {
      const { script } = event.detail;
      if (script) {
        // Get live data from stocks context
        const liveData = stocks[script.instrument_token] || {};
        
        // Create a mock stock object for the bottom sheet with proper structure
        const mockStock = {
          ...script,
          // Basic stock properties
          buyPrice: liveData.Bid || script.buyPrice || 0,
          sellPrice: liveData.Ask || script.sellPrice || 0,
          open: liveData.Open || script.open || 0,
          high: liveData.High || script.high || 0,
          low: liveData.Low || script.low || 0,
          close: liveData.Close || script.close || 0,
          lastTradedPrice: liveData.LTP || script.lastTradedPrice || 0,
          // Live data object that BottomSheet expects
          live: {
            Bid: liveData.Bid || 0,
            Ask: liveData.Ask || 0,
            Open: liveData.Open || 0,
            High: liveData.High || 0,
            Low: liveData.Low || 0,
            Close: liveData.Close || 0,
            LTP: liveData.LTP || 0,
            Prev_Close: liveData.Prev_Close || liveData.Close || 0
          }
        };
        setSelectedStock(mockStock);
        setShowBottomSheet(true);
      }
    };

    window.addEventListener('openTradeBottomSheet', handleOpenTradeBottomSheet);
    return () => {
      window.removeEventListener('openTradeBottomSheet', handleOpenTradeBottomSheet);
    };
  }, [stocks]);

  // Effect to update reordered scripts when filter changes in edit mode
  useEffect(() => {
    if (isEditMode && reorderedScripts.length > 0) {
      // Update reordered scripts with current filtered scripts
      setReorderedScripts([...sortedFilteredScripts]);
    }
  }, [filter, searchQuery, isEditMode]);

  // Filtered user scripts based on the selected filter and search query
  const filteredUserScripts = Object.values(userScripts).filter((stock) => {
    // Filter by exchange
    let matchesFilter = false;
    if (filter === "MCX FUT") {
      matchesFilter = stock.exchange === "FUTCOM";
    } else if (filter === "NSE FUT") {
      matchesFilter = stock.exchange === "FUTSTK";
    } else if (filter === "NSE OPT") {
      matchesFilter = stock.exchange === "OPTSTK";
    } else {
      matchesFilter = true;
    }
    
    // Filter by search query
    const matchesSearch = searchQuery === "" || 
      (stock.dispname && stock.dispname.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (stock.symbol && stock.symbol.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });

  // Sort filtered scripts according to saved order
  const getSortedScripts = (scripts) => {
    const exchange = filter === "MCX FUT" ? "FUTCOM" : 
                    filter === "NSE FUT" ? "FUTSTK" : 
                    filter === "NSE OPT" ? "OPTSTK" : "ALL";
    
    const savedOrderForExchange = getScriptOrder(exchange);
    
    if (savedOrderForExchange.length === 0) {
      return scripts;
    }

    // Create a map for quick lookup
    const orderMap = {};
    savedOrderForExchange.forEach((scriptId, index) => {
      orderMap[scriptId] = index;
    });

    // Sort scripts according to saved order
    return scripts.sort((a, b) => {
      const aOrder = orderMap[a.instrumentIdentifier];
      const bOrder = orderMap[b.instrumentIdentifier];
      
      // If both scripts are in saved order, sort by their order
      if (aOrder !== undefined && bOrder !== undefined) {
        return aOrder - bOrder;
      }
      
      // If only one script is in saved order, prioritize it
      if (aOrder !== undefined) return -1;
      if (bOrder !== undefined) return 1;
      
      // If neither script is in saved order, maintain original order
      return 0;
    });
  };

  const sortedFilteredScripts = getSortedScripts(filteredUserScripts);

  // Use reordered scripts if in edit mode, otherwise use sorted filtered scripts
  const displayScripts = isEditMode && reorderedScripts.length > 0 ? reorderedScripts : sortedFilteredScripts;

  const handleRefresh = () => {
    fetchUserScriptsExternal();
    //alert("Refresh");
    //refreshTradesAndPortfolio("trades");
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    console.log('Drag started for index:', index);
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverItem(index);
    console.log('Drag over index:', index);
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    console.log('Drop on index:', dropIndex, 'dragged from:', draggedItem);
    if (draggedItem === null || draggedItem === dropIndex) return;

    // Reorder the scripts
    const newOrder = [...displayScripts];
    const draggedScript = newOrder[draggedItem];
    newOrder.splice(draggedItem, 1);
    newOrder.splice(dropIndex, 0, draggedScript);

    // Update the reordered scripts state
    setReorderedScripts(newOrder);
    console.log('New order:', newOrder);
    
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleEditMode = () => {
    if (!isEditMode) {
      // Initialize reordered scripts with current sorted filtered scripts
      setReorderedScripts([...sortedFilteredScripts]);
    }
    setIsEditMode(!isEditMode);
  };

  const handleSaveOrder = () => {
    // Save the new order to localStorage
    const exchange = filter === "MCX FUT" ? "FUTCOM" : 
                    filter === "NSE FUT" ? "FUTSTK" : 
                    filter === "NSE OPT" ? "OPTSTK" : "ALL";
    
    const scriptIds = reorderedScripts.map(script => script.instrumentIdentifier);
    updateScriptOrder(exchange, scriptIds);
    
    console.log('Saving new order for', exchange, ':', scriptIds);
    setIsEditMode(false);
    setReorderedScripts([]);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setReorderedScripts([]);
    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Create Order Function (from BottomSheet)
  const createOrder = async (orderType, stock) => {
    setDisable(true);

    if (userDetails.mainAmount <= 0) {
      alert("You don't have balance.");
      setDisable(false);
      return;
    }

    if (quantity < 1) {
      alert("Quantity should be at least 1.");
      setDisable(false);
      return;
    }

    if (stock.exchange === "OPTSTK") {
      if (typeof lotSize === 'number' && lotSize % 1 !== 0) {
        alert("Lots should not be in points.");
        setDisable(false);
        return;
      }
    }

    if (orderType === 2 && stock.exchange === "OPTSTK") {
      if (Number(userDetails.availableMargin) < Number(userDetails.OPTSTK_balance)) {
        alert(`You don't have sufficient margin to sell.\nMinimum ${userDetails.OPTSTK_balance} is required.`);
        setDisable(false);
        return;
      }
    }

    if (stock.buyPrice <= 0 || stock.sellPrice <= 0) {
      alert("Price not updated, please refresh");
      setDisable(false);
      return;
    }

    if (userDetails.availableMargin <= 0) {
      alert("You do not have sufficient margin.");
      setDisable(false);
      return;
    }

    let orderAmount = 0;
    let isLimitOrder = orderType === 'Limit' || orderType === 'SL';

    if (isLimitOrder) {
      if (rate == stock.lastTradedPrice) {
        alert("Order price should not be equal to LTP.");
        setDisable(false);
        return;
      }

      orderAmount = quantity * rate;

      if (Number(rate) < Number(stock.lower_circuit) || Number(rate) > Number(stock.upper_circuit)) {
        alert("Order price is beyond daily price range.");
        setDisable(false);
        return;
      }

      if (orderType === 1) {
        if (rate >= stock.buyPrice) {
          alert("Order price must be less than bid price.");
          setDisable(false);
          return;
        }
      } else {
        if (rate <= stock.sellPrice) {
          alert("Order price should be greater than ask price.");
          setDisable(false);
          return;
        }
      }
    }

    if (orderType === 2) {
      if (stock.buyPrice <= 0) {
        alert("Price not updated, please refresh");
        setDisable(false);
        return;
      }
      orderAmount = quantity * stock.buyPrice;
    } else if (orderType === 1) {
      if (stock.sellPrice <= 0) {
        alert("Price not updated, please refresh");
        setDisable(false);
        return;
      }
      orderAmount = quantity * stock.sellPrice;
    }

    let requiredMargin = 0;

    if (stock.exchange === "FUTCOM") {
      if (userDetails.FUTCOM_margin_type === "script_wise") {
        const scriptMarginData = JSON.parse(userDetails.script_wise_margin[stock.scriptName]);
        const scriptInterdayValue = scriptMarginData.interaday;
        requiredMargin = scriptInterdayValue;
      } else {
        requiredMargin = orderAmount / userDetails.FUTCOM_intraday;
      }
    } else if (stock.exchange === "FUTSTK") {
      const futstkScripts = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY'];
      if (userDetails.FUTSTK_margin_type === "script_wise" && futstkScripts.includes(stock.scriptName)) {
        const scriptMarginData = JSON.parse(userDetails.script_wise_margin[stock.scriptName]);
        const scriptInterdayValue = scriptMarginData.interaday;
        requiredMargin = scriptInterdayValue;
      } else {
        requiredMargin = orderAmount / userDetails.FUTSTK_intraday;
      }
    } else if (stock.exchange === "OPTSTK") {
      requiredMargin = orderAmount / userDetails.OPTSTK_intraday;
    }

    if (orderType === 2 && stock.exchange === "OPTSTK") {
      requiredMargin = lotSize * userDetails.OPTSTK_balance;
    }

    if (requiredMargin > userDetails.availableMargin) {
      alert(`Can't place order.\nRequired margin: ${Number(requiredMargin).toFixed(2)}, current margin: ${Number(userDetails.availableMargin).toFixed(2)}`);
      setDisable(false);
      return;
    }

    if (lotSize <= 0 || lotSize <= 0.0) {
      alert("Please enter lot quantity.");
      setDisable(false);
      return;
    }

    stock.dispName = stock.dispname;

    try {
      const response = await fetch('https://papertrade.co.in/mediator_rfs.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stock,
          orderTypeBuySell: orderType === 1 ? 1 : 2,
          orderExecutionType: isLimitOrder ? 2 : 1,
          lots: lotSize,
          orderTransactionType: 1,
          market_type: `${stock.exchange}_intraday`,
          limitSlPrice: rate,
          orderDevice: 1,
          user_id: userDetails.clientId,
          parent_id: userDetails.parentId,
          ip: process.env.REACT_APP_USER_IP
        }),
      });
      const data = await response.json();

      if (!data.success) {
        setDisable(false);
        let msg = data.message;
        
        if (data.message === "open position") {
          msg = "Please close position from portfolio.";
        } else if (data.message === "open pending position") {
          msg = "You have a pending limit order on this script.\nYou can cancel it from Trades."
        } else if (data.message.includes("Blocked")) {
          msg = "Trading not allowed on this script.";
        }

        alert(msg);
      } else {
        setLotSize(1);
        setQuantity(100);
        setRate('');
        setExpandedCard(null);
        setDisable(false);

        var b_s = orderType === 1 ? 'Buy' : 'Sell';
        var msg = b_s + " " + Number(lotSize).toFixed(2) + " Lot(s) of " + stock.dispName + " @" + Number(data.orderPrice).toFixed(2);
        
        alert(msg);
        fetchUserScriptsExternal();
      }
    } catch (error) {
      console.error("Error:", error);
      setDisable(false);
    }
  };

  const memoizedUserDetails = useMemo(() => userDetails, [userDetails.availableMargin, userDetails.OPTSTK_sellAllow, userDetails.OPTSTK_balance]);

  const handleCloseExpandedView = useCallback(() => {
    setExpandedCard(null);
  }, []);

  // Expanded view component using BottomSheetMini
  const ExpandedView = React.memo(({ stock }) => {
    // const _stock = stocks[stock.instrument_token] || {};
    
    // // Only use static stock data, ignore live updates for form stability
    // const expandedStock = useMemo(() => {
    //   return {
    //     ...stock,
    //     // Keep original static values, don't override with live data
    //     buyPrice: stock.buyPrice || 0,
    //     sellPrice: stock.sellPrice || 0,
    //     open: stock.open || 0,
    //     high: stock.high || 0,
    //     low: stock.low || 0,
    //     close: stock.close || 0,
    //     lastTradedPrice: stock.lastTradedPrice || 0,
    //     // Add live data as separate property for display only
    //     liveData: _stock
    //   };
    // }, [stock.instrumentIdentifier, stock.lotsize, stock.exchange, stock.dispname, stock.expiry]);

    console.log("ExpandedView",stock);

    return (
      <div className="expanded-view-container">
        <BottomSheetMini 
          userDetails={memoizedUserDetails}
          stock={stock}
          isOpen={true}
          onClose={handleCloseExpandedView}
        />
      </div>
    );
  });

  return (
    <div className={`quotes-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      {showTv && <div className="video-container">
        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/IjLWWoYtNOg?si=Ua3aHQW9_Jahlj_Q"
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        ></iframe>
      </div>}

      {/* Control Panel */}
      <div className="control-panel">
        
        
        {/* <div className="toggle-controls">
          <div className="toggle-item">
            <span className="toggle-label">Pro View</span>
            <button 
              className={`toggle-switch ${proView ? 'active' : ''}`}
              onClick={toggleProView}
            />
          </div>
          <div className="action-buttons-container">
            <button 
              className="action-btn add-btn"
              onClick={() => navigate('/addScript')}
              title="Add Script"
            >
              <Add style={{ fontSize: '16px' }} />
              <span>Add</span>
            </button>
            <button 
              className="action-btn edit-btn"
              onClick={handleEditMode}
              title="Edit Scripts"
            >
              <Edit style={{ fontSize: '16px' }} />
              <span>Edit</span>
            </button>
          </div>
        </div> */}

        <div style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'center' }}>
          <div className="search-container" style={{ flex: 1, padding: '7px 5px' }}>
            <Search style={{ height: '20px', color: 'var(--text-muted)' }} />
            <input
              type="text"
              id="search-script-input"
              name="search-script"
              placeholder="Search Script"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            
          </div>
          <div className="pro-view-container">
            <div className="pro-view-toggle">
              <span className="pro-view-label">Standard</span>
              <div className="modern-toggle" onClick={toggleProView}>
                <div className={`toggle-slider ${proView ? 'pro-active' : ''}`}>
                  <div className="toggle-icon">
                    {proView ? <TrendingUp /> : <ViewList />}
                  </div>
                </div>
              </div>
              <span className="pro-view-label">Pro</span>
            </div>
          </div>
        </div>

      </div>

      {/* Market Segments */}
      <div className="market-segments">
        <div className="segment-tabs">
          <button 
            className={`segment-tab ${filter === "NSE FUT" ? 'active' : ''}`}
            onClick={() => setFilter("NSE FUT")}
          >
            NSE
          </button>
          <button 
            className={`segment-tab ${filter === "MCX FUT" ? 'active' : ''}`}
            onClick={() => setFilter("MCX FUT")}
          >
            MCX
          </button>
          <button 
            className={`segment-tab ${filter === "NSE OPT" ? 'active' : ''}`}
            onClick={() => setFilter("NSE OPT")}
          >
            OPT
          </button>
        </div>
        <div className="segment-actions">
          <button className="action-icon" onClick={handleToggle}>
            <LiveTv style={{ fontSize: '18px' }} />
          </button>
          <button className="action-icon" onClick={handleEditMode}>
            <Edit style={{ fontSize: '18px' }} />
          </button>
          <button className="action-icon" onClick={() => navigate('/addScript')}>
            <Add style={{ fontSize: '18px' }} />
          </button>
        </div>
      </div>

      {/* Quotes Table */}
      <div className="quotes-container">
        {userScripts.length === 0 ? (
          <div className="empty-state" style={{ marginTop: '20px' }}>
            <div className="empty-state-icon">
              <div className="icon-container">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className="empty-state-content">
              <h3 className="empty-state-title">No Scripts Added Yet</h3>
              <p className="empty-state-description">
                Start building your watchlist by adding your favorite trading scripts.
                Track live prices, analyze trends, and make informed decisions.
              </p>
              <div className="empty-state-actions">
                <button className="primary-action-btn" onClick={handleNavigation('/addScript')}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Add Your First Script
                </button>
                <button className="secondary-action-btn" onClick={() => setShowTv(true)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 3H6C7.10457 3 8 3.89543 8 5V19C8 20.1046 7.10457 21 6 21H2V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 7H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 11H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 15H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Watch Market News
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>

            {/* Quotes Table Header */}
            <div className="quotes-table-header" style={{ display: 'none' }}>
              <div className="script-column">Script</div>
              <div className="bid-column">Bid</div>
              <div className="ask-column">Ask</div>
            </div>

            {/* Edit Mode Controls */}
            {isEditMode && (
              <div className="edit-mode-controls">
                <button className="save-btn" onClick={handleSaveOrder}>
                  <Save style={{ height: '16px', marginRight: '8px' }} />
                  Save Order
                </button>
                <button className="cancel-btn" onClick={handleCancelEdit}>
                  <Cancel style={{ height: '16px', marginRight: '8px' }} />
                  Cancel
                </button>
              </div>
            )}

            {/* Commodities List */}
            <div className={`commodities-list ${isEditMode ? 'edit-mode' : ''} ${proView ? 'pro-view' : 'simple-view'}`}>
              {displayScripts.map((stock, index) => {
                const _stock = stocks[stock.instrument_token] || {};
                const change = _stock.Change || 0;
                const changePercent = _stock.ChangePercent || 0;
                const isPositive = change >= 0;
                
                return (
                  <div
                    key={stock.instrumentIdentifier}
                    draggable={isEditMode}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    className={`drag-item ${draggedItem === index ? 'dragging' : ''} ${dragOverItem === index ? 'drag-over' : ''}`}
                    style={{ 
                      cursor: isEditMode ? 'move' : 'default',
                      userSelect: isEditMode ? 'none' : 'auto'
                    }}
                  >
                    {isEditMode && (
                      <>
                        <div 
                          className="drag-handle" 
                          title="Drag to reorder"
                          draggable={false}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <span>⋮⋮</span>
                        </div>
                        <button
                          className="delete-button"
                          title="Remove from watchlist"
                          onClick={(e) => handleDeleteScript(stock, e)}
                        >
                          <Delete style={{ fontSize: '18px' }} />
                        </button>
                      </>
                    )}
                    <div 
                      style={{ 
                        marginLeft: isEditMode ? '30px' : '0px',
                        marginRight: isEditMode ? '0px' : '0px',
                        transition: 'margin 0.2s ease'
                      }}
                    >
                      <StockCard 
                        _stock={_stock} 
                        stock={stock}
                        onCardClick={isEditMode ? () => {} : handleCardClick}
                        isDarkMode={isDarkMode}
                        proView={proView}
                        positions={positions}
                        netPositions={netPositions}
                      />
                      {expandedCard === stock.instrumentIdentifier && (
                        <div className="expanded-view-container">
                          <BottomSheetMini 
                            userDetails={memoizedUserDetails}
                            stock={stock}
                            _stock={_stock}
                            isOpen={true}
                            onClose={handleCloseExpandedView}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Bottom Navigation is handled by NavBar.js */}

      {selectedStock && <BottomSheet isOpen={showBottomSheet} onClose={handleCloseSheet} userDetails={userDetails} stock={selectedStock} />}
    </div>
  );
};

export default StockList;
